<template>
  <v-container fluid>
    <div class="drawer-management pa-4">
      <!-- Header -->
      <div class="d-flex justify-space-between align-center mb-6">
        <div>
          <h1 class="text-h4 font-weight-bold">
            {{ $t("cash_drawer_management") }}
          </h1>
          <p class="text-subtitle-1 grey--text">
            {{ $t("cash_drawer_management_desc") }}
          </p>
        </div>
        <v-btn
          v-if="canManage"
          color="primary"
          depressed
          class="rounded-lg white--text"
          @click="openAddDrawerDialog"
        >
          <v-icon left>fal fa-plus</v-icon>
          {{ $t("add_cash_drawer") }}
        </v-btn>
      </div>

      <!-- Drawers List -->
      <v-card class="ap-card" outlined elevation="0">
        <v-card-title class="py-3 px-4 border-bottom d-flex align-center justify-space-between">
          <div class="d-flex align-center" style="gap: 10px;">
            <span class="ap-card-accent"></span>
            <span class="font-weight-bold text-subtitle-2">{{ $t("cash_drawers") }}</span>
          </div>
          <v-chip small color="primary" outlined class="font-weight-bold">
            {{ drawers.length }} {{ $t("items") }}
          </v-chip>
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="drawers"
          :loading="loading"
          class="elevation-0 app-table"
          :no-data-text="$t('no_data')"
        >
          <!-- Drawer Name -->
          <template v-slot:item.name="{ item }">
            <div class="d-flex align-center py-2">
              <v-icon color="primary" size="20" class="mr-3">
                fal fa-money-bill-wave
              </v-icon>
              <div class="font-weight-medium">{{ item.name }}</div>
            </div>
          </template>

          <!-- Status -->
          <template v-slot:item.status="{ item }">
            <v-chip :color="statusColor(item.status)" text-color="white" small class="font-weight-bold">
              {{ $t(item.status) }}
            </v-chip>
            <div
              v-if="item.status === 'in_use' && item.active_shift"
              class="text-caption grey--text mt-1"
            >
              {{ $t("held_by") }}:
              {{ item.active_shift.user ? (item.active_shift.user.first_name + ' ' + (item.active_shift.user.last_name || '')) : '—' }}
            </div>
          </template>

          <!-- Actions -->
          <template v-if="canManage" v-slot:item.actions="{ item }">
            <div class="d-flex gap-1">
              <v-btn
                icon
                small
                @click="editDrawer(item)"
                :title="$t('edit')"
              >
                <v-icon small color="primary">fal fa-edit</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="error"
                :disabled="item.status === 'in_use'"
                @click="deleteDrawer(item)"
                :title="
                  item.status === 'in_use'
                    ? $t('cash_drawer_in_use_cannot_delete')
                    : $t('delete')
                "
              >
                <v-icon small>fal fa-trash</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <!-- Add/Edit Drawer Dialog -->
      <app-custom-dialog
        v-model="showDrawerDialog"
        :title="editingDrawer ? 'edit_cash_drawer' : 'add_cash_drawer'"
        :footer="true"
        width="500px"
        :submit-text="editingDrawer ? 'update' : 'save'"
        @submit="saveDrawer"
      >
        <div slot="activator">
          <v-form ref="drawerForm" v-model="validForm">
            <v-row>
              <v-col cols="12">
                <TTextField
                  v-model="drawerForm.name"
                  label="cash_drawer_name"
                  :error-messages="nameError"
                  @input="onNameInput"
                />
              </v-col>
            </v-row>
          </v-form>
        </div>
      </app-custom-dialog>
    </div>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import TTextField from "@/components/global/TextField.vue";
import { toast, confirm } from "@/controllers/alert";

// Only owner/manager/admin configure the branch's physical drawer roster.
const MANAGE_ROLES = [1, 2, 3];

export default {
  name: "CashDrawerManagement",
  components: {
    TTextField,
  },
  data() {
    return {
      loading: false,
      saving: false,
      showDrawerDialog: false,
      editingDrawer: null,
      validForm: false,
      drawers: [],
      nameError: [],
      drawerForm: {
        name: "",
      },
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
    }),
    canManage() {
      const r = Number(this.user?.role);
      return MANAGE_ROLES.includes(r);
    },
    headers() {
      const cols = [
        { text: this.$t("cash_drawer_name"), value: "name", sortable: true },
        { text: this.$t("status"), value: "status", sortable: true },
      ];
      if (this.canManage) {
        cols.push({
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          width: 100,
        });
      }
      return cols;
    },
  },
  methods: {
    statusColor(status) {
      if (status === "available") return "success";
      if (status === "in_use") return "warning";
      return "grey";
    },
    validateDrawerName() {
      if (!this.drawerForm.name || this.drawerForm.name.trim() === "") {
        this.nameError = [this.$t("field_required")];
        return false;
      }
      this.nameError = [];
      return true;
    },
    onNameInput() {
      if (this.nameError && this.drawerForm.name.trim()) {
        this.nameError = [];
      }
    },
    async loadData() {
      this.loading = true;
      try {
        const { data } = await this.$http.get("cash_drawers");

        if (data && data.status && data.data) {
          this.drawers = data.data;
        } else {
          this.drawers = [];
        }
      } catch (error) {
        console.error("Error loading cash drawers:", error);
        toast(this, "error_loading_cash_drawers", "error");
      } finally {
        this.loading = false;
      }
    },
    openAddDrawerDialog() {
      this.editingDrawer = null;
      this.nameError = [];
      this.drawerForm = { name: "" };
      this.showDrawerDialog = true;
    },
    editDrawer(drawer) {
      this.editingDrawer = drawer;
      this.nameError = [];
      this.drawerForm = { name: drawer.name };
      this.showDrawerDialog = true;
    },
    async saveDrawer() {
      if (!this.validateDrawerName()) {
        toast(this, "field_required", "error");
        return;
      }

      this.saving = true;
      try {
        const formData = {
          name: this.drawerForm.name.trim(),
          store_id: this.user?.stores?.id || this.user?.store_id,
          branch_id: this.user?.branch_id,
        };

        if (this.editingDrawer) {
          const { data } = await this.$http.put(
            `cash_drawers/${this.editingDrawer.id}`,
            formData
          );

          if (data && data.status) {
            toast(this, "cash_drawer_updated_success", "success");
            await this.loadData();
            this.showDrawerDialog = false;
            this.resetForm();
          }
        } else {
          const { data } = await this.$http.post("cash_drawers", formData);

          if (data && data.status) {
            toast(this, "cash_drawer_created_success", "success");
            await this.loadData();
            this.showDrawerDialog = false;
            this.resetForm();
          }
        }
      } catch (error) {
        console.error("Error saving cash drawer:", error);
        const errorMessage =
          error.response?.data?.message || "error_saving_cash_drawer";
        toast(this, errorMessage, "error");
      } finally {
        this.saving = false;
      }
    },
    async deleteDrawer(drawer) {
      const { isConfirmed } = await confirm({
        props: this,
        title: "delete_cash_drawer",
        text: "delete_cash_drawer_confirm",
      });

      if (!isConfirmed) return;

      try {
        const { data } = await this.$http.delete(
          `cash_drawers/${drawer.id}`,
          { data: { store_id: this.user?.stores?.id || this.user?.store_id, branch_id: this.user?.branch_id } }
        );

        if (data && data.status) {
          toast(this, "cash_drawer_deactivated_success", "success");
          await this.loadData();
        }
      } catch (error) {
        console.error("Error deactivating cash drawer:", error);
        const errorMessage =
          error.response?.data?.message || this.$t("error_deleting_cash_drawer");
        toast(this, errorMessage, "error");
      }
    },
    resetForm() {
      this.drawerForm = { name: "" };
      this.editingDrawer = null;
      this.nameError = [];
      if (this.$refs.drawerForm) {
        this.$refs.drawerForm.resetValidation();
      }
    },
  },
  async created() {
    await this.loadData();
  },
};
</script>

<style scoped>
.drawer-management {
  max-width: 1400px;
  margin: 0 auto;
}

.ap-card {
  border-radius: 16px !important;
  border: 1px solid #e2e8f0 !important;
  background-color: #ffffff !important;
}

.ap-card-accent {
  width: 3px;
  height: 15px;
  border-radius: 2px;
  background: var(--v-primary-base, #5b39c9);
  display: inline-block;
  flex-shrink: 0;
}

.gap-1 {
  gap: 4px;
}

@media (max-width: 600px) {
  .drawer-management {
    padding: 8px;
  }

  .d-flex.justify-space-between.align-center {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
}
</style>
